import type { ChatPascoDTO } from '@src/application/chat/dto/chat.dto';

/**
 * Chat Message Value Object
 * Represents a single message in the chat conversation
 */
export class ChatMessage {
    private constructor(
        private readonly _id: string,
        private readonly _role: 'user' | 'assistant',
        private readonly _content: string,
        private readonly _timestamp: Date,
        private readonly _pascoIds: string[],
        private readonly _mostRelevantPascos: ChatPascoDTO[],
        private readonly _leastRelevantPascos: ChatPascoDTO[]
    ) {
        // Ensure arrays are always arrays for backward compatibility
        this._mostRelevantPascos = this._mostRelevantPascos || [];
        this._leastRelevantPascos = this._leastRelevantPascos || [];
    }

    static create(props: {
        id: string;
        role: 'user' | 'assistant';
        content: string;
        timestamp: Date;
        pascoIds?: string[];
        mostRelevantPascos?: ChatPascoDTO[];
        leastRelevantPascos?: ChatPascoDTO[];
    }): ChatMessage {
        const mostRelevantPascos = props.mostRelevantPascos || [];
        const leastRelevantPascos = props.leastRelevantPascos || [];
        const allPascos = [...mostRelevantPascos, ...leastRelevantPascos];
        const pascoIds = props.pascoIds || allPascos.map(p => p.pasco_id);

        return new ChatMessage(
            props.id,
            props.role,
            props.content,
            props.timestamp,
            pascoIds,
            mostRelevantPascos,
            leastRelevantPascos
        );
    }

    get id(): string {
        return this._id;
    }

    get role(): 'user' | 'assistant' {
        return this._role;
    }

    get content(): string {
        return this._content;
    }

    get timestamp(): Date {
        return this._timestamp;
    }

    get pascoIds(): string[] {
        return this._pascoIds;
    }

    get mostRelevantPascos(): ChatPascoDTO[] {
        return this._mostRelevantPascos;
    }

    get leastRelevantPascos(): ChatPascoDTO[] {
        return this._leastRelevantPascos;
    }

    get allPascos(): ChatPascoDTO[] {
        return [...(this._mostRelevantPascos || []), ...(this._leastRelevantPascos || [])];
    }

    get isUserMessage(): boolean {
        return this._role === 'user';
    }

    get isAssistantMessage(): boolean {
        return this._role === 'assistant';
    }
}
